import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite';

import { childrenPropType, stylePropType, locationShape, STANDARD_TEXT, TSS_GREEN, TSS_DARK_BLUE, TSS_RED, TSS_BLUE_1 } from 'tss-core-lib-common';

var styles = void 0;

var RegularButton = function RegularButton(_ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      style = _ref.style,
      containerStyle = _ref.containerStyle,
      containerClassName = _ref.containerClassName,
      spinnerStyle = _ref.spinnerStyle,
      location = _ref.location,
      loading = _ref.loading,
      onClick = _ref.onClick;

  var child = React.createElement(
    'div',
    { className: css(STANDARD_TEXT, styles.inner, style || {}) },
    children,
    loading ? React.createElement('span', { className: css(styles.spinner, spinnerStyle) }) : null
  );

  return location ? React.createElement(
    NavLink,
    {
      to: location,
      className: 'tss-button ' + containerClassName + ' ' + css(styles.container, containerStyle, disabled && styles.disabled),
      disabled: disabled
    },
    child
  ) : React.createElement(
    'button',
    {
      className: 'tss-button ' + containerClassName + ' ' + css(styles.container, containerStyle, disabled && styles.disabled),
      disabled: disabled,
      onClick: onClick
    },
    child
  );
};

RegularButton.propTypes = {
  children: childrenPropType.isRequired,
  disabled: PropTypes.bool,
  style: stylePropType,
  containerClassName: PropTypes.string,
  containerStyle: stylePropType,
  spinnerStyle: stylePropType,
  location: locationShape,
  loading: PropTypes.bool,
  onClick: PropTypes.func
};

RegularButton.defaultProps = {
  disabled: false,
  style: null,
  containerStyle: null,
  spinnerStyle: null,
  location: null,
  loading: false,
  onClick: null,
  containerClassName: ''
};

styles = StyleSheet.create({
  container: {
    margin: 0,
    padding: 0,
    outline: 0,
    position: 'relative',
    border: 0,
    background: 'none',
    textDecoration: 'none',
    display: 'inline-block',
    cursor: 'default',

    ':nth-of-type(1n) + .tss-button': {
      marginLeft: '1.6rem'
    },

    ':nth-of-type(1n) + .tss-button.tss-button--no-margin': {
      marginLeft: 0
    }
  },

  tertiaryContainer: {
    padding: '0 1.6rem'
  },

  inner: {
    padding: '0 4rem',
    height: '5.3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    border: 0,
    cursor: 'pointer'
  },

  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },

  primary: {
    backgroundColor: TSS_GREEN(),
    color: 'white'
  },

  danger: {
    backgroundColor: TSS_RED(),
    color: 'white'
  },

  secondary: {
    backgroundColor: TSS_DARK_BLUE(),
    color: 'white'
  },

  tertiary: {
    backgroundColor: 'transparent',
    color: TSS_DARK_BLUE(),
    padding: 0,

    ':hover': {
      textDecoration: 'underline'
    }
  },

  invertedPrimary: {
    color: TSS_GREEN(),
    fontSize: '1.6rem',
    fontWeight: 600,
    backgroundColor: 'white',
    padding: '1.3rem 4rem 1.4rem',
    outline: 0,
    margin: 0,
    border: '1px solid ' + TSS_BLUE_1(),
    boxShadow: '0 1px 0 0 ' + TSS_BLUE_1() + ', 0 2px 10px 0 rgba(0,0,0,0.06)',
    cursor: 'pointer',

    ':hover': {
      boxShadow: '0 1px 0 0 ' + TSS_BLUE_1() + ', 0 2px 10px 0 rgba(0,0,0,0.14)'
    }
  },

  icon: {
    border: 0,
    outline: 0,
    backgroundColor: 'transparent',
    display: 'block',
    height: 'auto',
    padding: 0,
    minWidth: 0
  },

  spinner: {
    position: 'absolute',
    width: '0.4rem',
    height: '2rem',
    top: '50%',
    right: '2rem',
    marginTop: '-1rem',
    backgroundColor: 'black',
    animationName: [{
      from: {
        transform: 'rotate(0deg)'
      },
      to: {
        transform: 'rotate(360deg)'
      }
    }],
    animationDuration: '0.8s',
    animationIterationCount: 'infinite'
  },

  primarySpinner: {
    backgroundColor: 'white'
  },
  secondarySpinner: {
    backgroundColor: 'white'
  },
  tertiarySpinner: {
    right: '0.5rem',
    backgroundColor: TSS_DARK_BLUE()
  }
});

var PrimaryButton = function PrimaryButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.primary, props.style], spinnerStyle: styles.primarySpinner }));
};
var InvertedPrimaryButton = function InvertedPrimaryButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.invertedPrimary, props.style], spinnerStyle: styles.primarySpinner }));
};

var DangerButton = function DangerButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.danger, props.style], spinnerStyle: styles.primarySpinner }));
};
var SecondaryButton = function SecondaryButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.secondary, props.style], spinnerStyle: styles.secondarySpinner }));
};
var TertiaryButton = function TertiaryButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.tertiary, props.style], containerStyle: [styles.tertiaryContainer, props.containerStyle], spinnerStyle: styles.tertiarySpinner }));
};
var IconButton = function IconButton(props) {
  return React.createElement(RegularButton, Object.assign({}, props, { style: [styles.icon, props.style] }));
};

var propTypes = {
  style: stylePropType,
  containerStyle: stylePropType // eslint-disable-line
};

var defaultProps = {
  style: null,
  containerStyle: null
};

PrimaryButton.propTypes = propTypes;
PrimaryButton.defaultProps = defaultProps;

InvertedPrimaryButton.propTypes = propTypes;
InvertedPrimaryButton.defaultProps = defaultProps;

DangerButton.propTypes = propTypes;
DangerButton.defaultProps = defaultProps;

SecondaryButton.propTypes = propTypes;
SecondaryButton.defaultProps = defaultProps;

TertiaryButton.propTypes = propTypes;
TertiaryButton.defaultProps = defaultProps;

IconButton.propTypes = propTypes;
IconButton.defaultProps = defaultProps;

export { RegularButton, DangerButton, PrimaryButton, SecondaryButton, TertiaryButton, IconButton, InvertedPrimaryButton };